import Swal from "sweetalert2";
import styled from 'styled-components';
import { Spinner } from 'react-bootstrap';
import EditIcon from "mdi-react/EditIcon";
import Panel from "@/shared/components/Panel";
import DeleteIcon from "mdi-react/DeleteIcon";
import RestartIcon from 'mdi-react/RestartIcon';
import React, { useState, useEffect } from 'react';
import AdminInterface from '@/utils/AdminInterface';
import { Typography, Tooltip } from "@mui/material";
import withReactContent from "sweetalert2-react-content";
import ProgressCloseIcon from 'mdi-react/ProgressCloseIcon';
import ProgressCheckIcon from 'mdi-react/ProgressCheckIcon';
import ProgressClockIcon from 'mdi-react/ProgressClockIcon';
import EditCreateAssetRequest from "./EditCreateAssetRequest";
import { paddingLeft, paddingRight } from '@/utils/directions';
import ProgressSpannerIcon from 'mdi-react/ProgressSpannerIcon';
import SimpleModal from '@/shared/components/Modal/SimpleModal';
import ReactTableBase from '@/shared/components/table/ReactTableBase';
import { colorAdditional, colorGreenHover, colorAccent } from '@/utils/palette';
import InformationCircleOutlineIcon from 'mdi-react/InformationCircleOutlineIcon';
import { BatchJobStorageEnum, BatchJobVCPUsEnum, JobStatusEnum, CreateAssetRequestTypeEnum } from '../../../../../Enums';

const CartTableButton = styled.button`
  background: transparent;
  border: none;
  line-height: 14px;
  ${paddingLeft}: 20px;
  ${paddingRight}: 0;
  padding-top: 0;
  padding-bottom: 0;
  color: ${colorAdditional};
  cursor: pointer;
  transition: all 0.3s;

  svg {
    height: 24px;
    width: 24px;
    fill: '#ddd';
    transition: all 0.3s;
  }

  svg:hover {
    fill: ${colorGreenHover};
  }

  .orange-hover:hover {
    fill: #e9c886;
  }

  .red-hover:hover {
    fill: #e98686;
  }
`;

const TableSpinner = styled(Spinner)`
  color: ${colorAccent};
`;

const CreateAssetRequestList = () => {
    const [showLoader, setShowLoader] = useState(true);
    const [selectedCreateAssetRequest, setSelectedCreateAssetRequest] = useState();
    const [requestList, setRequestList] = useState({ tableHeaderData: [], tableRowsData: [] });
    const tableConfig = {
        isEditable: false,
        isSortable: true,
        isResizable: false,
        withPagination: false,
        withSearchEngine: true,
        placeholder: 'Search...',
    };

    const handleRetry = (request) => {
        Swal.fire({
            title: 'Warning!',
            text: `Do you want to re-run the request? This will start a batch job in the background.`,
            icon: 'warning',
            confirmButtonText: 'Yes!',
            cancelButtonText: 'No!',
            showCancelButton: true
        }).then((result) => {
            if (result.isConfirmed) {
                AdminInterface.instance.retryCreateAssetRequests(request.id).then(() => {
                    withReactContent(Swal).fire({ title: "Success", text: "Request is started again!" });
                    GetRequests();
                }).catch((error) => {
                    withReactContent(Swal).fire({ title: "Error", html: error.response?.data?.detail });
                });
            }
        });
    };

    const handleDelete = (request) => {
        Swal.fire({
            title: 'Warning!',
            text: `Do you want to delete the request? It is not reversible!`,
            icon: 'warning',
            confirmButtonText: 'Yes!',
            cancelButtonText: 'No!',
            showCancelButton: true
        }).then((result) => {
            if (result.isConfirmed) {
                AdminInterface.instance.deleteCreateAssetRequest(request.id).then(() => {
                    withReactContent(Swal).fire({ title: "Success", text: "Request is deleted!" });
                    GetRequests();
                }).catch((error) => {
                    withReactContent(Swal).fire({ title: "Error", html: error.response?.data?.detail });
                });
            }
        });
    };

    const handleModalClose = () => {
        setSelectedCreateAssetRequest();
        GetRequests();
    }

    const handleEdit = (request) => {
        setSelectedCreateAssetRequest(request);
    };

    const GetRequests = () => {
        setShowLoader(true);
        AdminInterface.instance.getCreateAssetRequests().then((response) => {
            const columns = [
                {
                    accessor: 'id',
                    Header: 'ID',
                },
                {
                    accessor: 'collection',
                    Header: 'Collection',
                },
                {
                    accessor: 'mission',
                    Header: 'Mission',
                },
                {
                    accessor: 'tiles_directory_uri',
                    Header: 'Tiles Directory URI',
                },
                {
                    accessor: 'type',
                    Header: 'Type'
                },
                {
                    accessor: 'start_datetime',
                    Header: 'Start Datetime'
                },
                {
                    accessor: 'status_datetime',
                    Header: 'Status Datetime'
                },
                {
                    accessor: 'info',
                    Header: 'Info',
                    disableGlobalFilter: true,
                    disableSortBy: true,
                },
                {
                    accessor: 'status',
                    Header: 'Status',
                    disableGlobalFilter: true,
                    disableSortBy: true,
                },
                {
                    accessor: 'retry',
                    Header: 'Re-run',
                    disableGlobalFilter: true,
                    disableSortBy: true,
                },
                {
                    accessor: 'edit',
                    Header: 'Edit',
                    disableGlobalFilter: true,
                    disableSortBy: true,
                },
                {
                    accessor: 'delete',
                    Header: 'Delete',
                    disableGlobalFilter: true,
                    disableSortBy: true,
                }
            ];
            const data = [];
            response.data.sort((a, b) => b.id - a.id);
            response.data.forEach(request => {
                data.push({
                    id: request.id,
                    tiles_directory_uri: request.tiles_directory_uri,
                    mission: request.mission.name,
                    collection: request.mission.collection.name,
                    type: request.type === undefined ? '' : Object.keys(CreateAssetRequestTypeEnum).find(type => CreateAssetRequestTypeEnum[type] === request.type),
                    start_datetime: request.start_datetime ? new Date(`${request.start_datetime}`).toLocaleString() : '',
                    status_datetime: request.status_datetime ? new Date(`${request.status_datetime}`).toLocaleString() : '',
                    info: <Tooltip title={
                        <Typography fontSize={12}>
                            Job ID: {request.job_id}<br/>
                            User Requested: {request.user_requested}<br/>
                            Hosting Bucket: {request.hosting_bucket}<br/>
                            VCPUs: {Object.keys(BatchJobVCPUsEnum).find(key => BatchJobVCPUsEnum[key] === request.batch_job_vcpus)}<br/>
                            Storage: {Object.keys(BatchJobStorageEnum).find(key => BatchJobStorageEnum[key] === request.batch_job_storage)}<br/>
                            Footprint: {request.footprint_shapefile_uri ? request.footprint_shapefile_uri : 'None'}<br/>
                            Source SRS: {request.source_srs ? `EPSG:${request.source_srs}` : 'None'}<br/>
                            Require Rotation: {request.rotate_by_orientation ? 'True' : 'False'}
                        </Typography>
                    } placement='bottom-start'><span><InformationCircleOutlineIcon color='#ddd' /></span></Tooltip>,
                    status: request.status === JobStatusEnum.completed ? <Tooltip title={<Typography fontSize={12}>Completed</Typography>} placement='bottom-start'><span><ProgressCheckIcon color='#b8e986' /></span></Tooltip> :
                            request.status === JobStatusEnum.failed ? <Tooltip title={<Typography fontSize={12}>Failed</Typography>} placement='bottom-start'><span><ProgressCloseIcon color='#f88e7d' /></span></Tooltip> :
                            request.status === JobStatusEnum.running ? <Tooltip title={<Typography fontSize={12}>Running</Typography>} placement='bottom-start'><span><ProgressClockIcon color='#f6da6e' /></span></Tooltip> :
                            <Tooltip title={<Typography fontSize={12}>Not Started</Typography>} placement='bottom-start'><span><ProgressSpannerIcon color='#ddd' /></span></Tooltip>,
                    retry: request.status === JobStatusEnum.failed ? <Tooltip title={<Typography fontSize={12}>Rerun the request</Typography>}><span><CartTableButton type="button" onClick={() => handleRetry(request)}><RestartIcon className="orange-hover" color='#ddd' /></CartTableButton></span></Tooltip> : '',
                    edit: (request.status !== JobStatusEnum.running && request.status !== JobStatusEnum.completed) ? <Tooltip title={<Typography fontSize={12}>Edit</Typography>}><CartTableButton type="button" onClick={() => handleEdit(request)}><EditIcon className="orange-hover" color='#ddd' /></CartTableButton></Tooltip> : '',
                    delete: request.status !== JobStatusEnum.running ? <Tooltip title={<Typography fontSize={12}>Delete</Typography>}><CartTableButton type="button" onClick={() => handleDelete(request)}><DeleteIcon className="red-hover" color='#ddd' /></CartTableButton></Tooltip> : ''
                });
            });

            setRequestList({ tableHeaderData: columns, tableRowsData: data });
        }).catch((error) => {
            withReactContent(Swal).fire({ title: "Error", text: "Error in loading assets." });
        }).finally(() => {
            setShowLoader(false);
        });
    }

    useEffect(() => {
        GetRequests();
    }, []);

    return (
        <Panel lg={12} xl={12} md={12} title='Create Asset Requests List' refreshRequest={() => GetRequests()} dismissButton={() => {return}}>
            {!showLoader &&
            <>
                <ReactTableBase columns={requestList.tableHeaderData} data={requestList.tableRowsData} tableConfig={tableConfig} />
                <SimpleModal isActive={selectedCreateAssetRequest !== undefined} onClose={handleModalClose}>
                    <EditCreateAssetRequest request={selectedCreateAssetRequest} />
                </SimpleModal>
            </>
            }
            {showLoader && (<div className="text-center"><TableSpinner animation="border" /></div>)}
        </Panel>
    );
};

export default CreateAssetRequestList;